import React, { useMemo, useState } from 'react';
import useOneWayIn from '../../hooks/use-one-way-in';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, useUser } from 'src/features/authorization';
import { PageParams } from '../types/PageParams';
import { useForm } from 'react-hook-form';
import { OneWayInStatus, SivaContractCreateDto, SivaPhase, SivaService } from 'src/api/v2';
import { useAvailablePhases } from 'src/features/siva/ideas-and-companies/hooks/use-available-phases';
import { useTranslation } from 'react-i18next';
import { useSivaContracts } from 'src/features/siva/ideas-and-companies/hooks/use-siva-contracts';
import SelectPhase from 'src/features/siva/ideas-and-companies/components/company/SelectPhase';
import NewCompanyContract from 'src/features/siva/ideas-and-companies/components/company/NewCompanyContract';
import { ONE_WAY_IN } from '../../routes';
import { toast } from 'react-toastify';
import { ONE_WAY_IN_OPPORTUNITY_SIVA_CONTRACT_CONNECTION } from 'src/constants/hook-keys';
import { useQuery } from '@tanstack/react-query';
import SivaContractOverviewCard from 'src/features/siva/ideas-and-companies/components/SivaContractOverviewCard';
import { Spinner } from '@innovationnorway/cl-react';
import { HOTJAR_DECLARATIONS } from 'src/features/tracking/constants';
import useHotjar from 'src/hooks/use-hotjar';

const ProgramAdmission = () => {
  const { formId } = useParams<PageParams>();
  const { checkAccess } = useAuth();
  const hotjar = useHotjar();

  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  const { t: tSiva } = useTranslation('siva');
  const { user, isAadUser } = useUser();
  const { updateOneWayInOpportunityStatus } = useOneWayIn(formId);

  const { oneWayInGroupedFormWOEnrollmentsQuery, oneWayInOpportunityById } = useOneWayIn(formId);
  const navigate = useNavigate();

  const opportunity = useMemo(() => {
    if (!oneWayInOpportunityById.data) return undefined;

    return oneWayInOpportunityById.data;
  }, [oneWayInOpportunityById.data]);

  const [service] = useState(() => new SivaService());
  const organizationNumber =
    opportunity?.organizationNumber?.startsWith('8') || opportunity?.organizationNumber?.startsWith('9')
      ? opportunity?.organizationNumber
      : undefined;

  const { data, isLoading } = useQuery({
    queryKey: [ONE_WAY_IN_OPPORTUNITY_SIVA_CONTRACT_CONNECTION, opportunity?.id],
    queryFn: () =>
      service.findSivaContractDetails(opportunity?.clusterMemberId, organizationNumber, opportunity?.id),
    enabled: opportunity?.id !== undefined,
  });

  const availablePhases = useAvailablePhases(organizationNumber);
  const form = useForm<SivaContractCreateDto>({
    defaultValues: {
      sivaPhase: isBusinessGarden ? SivaPhase.Targeted : SivaPhase.Idea,
      name: opportunity?.title,
      ideaSource: 'Annet',
      organizationNumber: organizationNumber,
      contactFirstName: opportunity?.customerName?.split(' ')[0],
      contactLastName: opportunity?.customerName?.split(' ').slice(1).join(' '),
      contactEmail: oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.epost,
      advisorId: user?.clusterUserId,
      oneWayInOpportunityId: opportunity?.id,
    },
  });

  const { mutations } = useSivaContracts();

  const handleOnSubmit = form.handleSubmit(async (input) => {
    if (!input.name) {
      input.name = '';
    }

    if (!input.notes) {
      input.notes = '';
    }
    await mutations.create
      .mutateAsync(input)
      .then(() => {
        updateOneWayInOpportunityStatus.mutateAsync({
          oneWayInOpportunityId: input.oneWayInOpportunityId ?? '',
          referenceClusterId: undefined,
          description: input.notes,
          status: OneWayInStatus.Active,
          closedReason: undefined,
          registeredServices: [],
          recommendedServices: [],
          clusterUserId: isAadUser ? undefined : user?.clusterUserId,
          clusterContactId: undefined,
          handlersName: user?.displayName,
          sendFollowUpReminderOn: undefined,
          admittedToProgram: true,
          sivaPhase: input.sivaPhase,
          deliveredOn: undefined,
        });
        toast.success(
          input.name +
            ' med fase ' +
            tSiva(`phase.${input.sivaPhase}`).toLocaleLowerCase() +
            ' ble lagt til.',
          {
            autoClose: false,
            position: 'bottom-center',
          },
        );
        hotjar.log(
          `${HOTJAR_DECLARATIONS.UMUX.id} ${isBusinessGarden ? HOTJAR_DECLARATIONS.UMUX.actions.OPPORTUNITY_TO_TARGETED : HOTJAR_DECLARATIONS.UMUX.actions.OPPORTUNITY_TO_IDEA}`,
        );
        window.scrollTo(0, 0);
        navigate(ONE_WAY_IN.DETAILS.BASE(formId || ''));
      })
      .catch(() =>
        toast.error(tSiva('error.support.message'), {
          autoClose: false,
          position: 'bottom-center',
        }),
      );
  });
  if (isLoading) {
    return (
      <div className="stack">
        <h2>
          {isBusinessGarden ? tSiva('newCompany.title.businessGarden') : tSiva('newCompany.title.incubator')}
        </h2>
        <Spinner aria-label="Laster" />
      </div>
    );
  }
  return (
    <div className="stack">
      <h2>
        {isBusinessGarden ? tSiva('newCompany.title.businessGarden') : tSiva('newCompany.title.incubator')}
      </h2>
      {data ? (
        <div>
          <p>Det finnes en avtale med følgende informasjon:</p>
          <SivaContractOverviewCard sivaContract={data} />
        </div>
      ) : organizationNumber?.length !== 9 && isBusinessGarden ? (
        <p>
          For å ta kunden opp i næringshageprogrammet må kunden ha opprettet en bedrift. Du kan knytte en
          bedrift til henvendelsen fra toppmenyen.
        </p>
      ) : (
        <form onSubmit={handleOnSubmit}>
          {!isBusinessGarden && (
            <SelectPhase
              form={form}
              availablePhases={availablePhases}
              stepBackward={undefined}
              stepForward={undefined}
              headingLevel="h3"
            />
          )}
          <NewCompanyContract
            key={organizationNumber}
            organizationNumber={organizationNumber!}
            form={form}
            isSaving={mutations.create.isLoading}
            stepBackward={undefined}
            stepForward={undefined}
            headingLevel="h3"
          />
        </form>
      )}
    </div>
  );
};
export default ProgramAdmission;
